import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material'
import { interventionConfigsAPI, strategiesAPI, riskCodesAPI } from '../../services/api'
import StrategyEditorDialog from './StrategyEditorDialog'

// Enabled/disabled indicator
const enabledStyles = {
  true: { color: '#16a34a', label: 'Enabled' },
  false: { color: '#71717a', label: 'Disabled' },
}

function EnabledIndicator({ enabled, onClick, clickable }) {
  const style = enabledStyles[String(enabled)]
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { opacity: 0.8 } : {},
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="caption" sx={{ color: style.color, fontSize: '0.75rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function StrategiesTab({ interventionId, canEdit, onError }) {
  const [attachedStrategies, setAttachedStrategies] = useState([])
  const [interventionTracks, setInterventionTracks] = useState([])
  const [interventionRiskCodes, setInterventionRiskCodes] = useState([])
  const [allRiskCodes, setAllRiskCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingStrategy, setEditingStrategy] = useState(null)

  // Drag-to-reorder state
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dropTargetIndex, setDropTargetIndex] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [attached, tracks, ivRiskCodes, allRc] = await Promise.all([
        interventionConfigsAPI.getStrategies(interventionId),
        interventionConfigsAPI.getTracks(interventionId),
        interventionConfigsAPI.getRiskCodes(interventionId).catch(() => []),
        riskCodesAPI.getAll().then(res => res.data || []).catch(() => []),
      ])
      setAttachedStrategies(attached)
      setInterventionTracks(tracks || [])
      setInterventionRiskCodes(ivRiskCodes || [])
      setAllRiskCodes(allRc || [])
    } catch (err) {
      console.error('Error fetching strategies:', err)
      onError?.('Failed to load composition rules')
    } finally {
      setLoading(false)
    }
  }, [interventionId, onError])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleOpenEditor = async (strategy = null) => {
    // Refresh the intervention's attached risk codes right before opening the
    // editor. RiskCodesTab and this tab are mounted as siblings, so a code
    // attached/removed in the Risk Codes section doesn't propagate here on its
    // own. Pulling fresh data on open ensures the editor's risk-code dropdown
    // reflects the current attachment set.
    try {
      const fresh = await interventionConfigsAPI.getRiskCodes(interventionId)
      setInterventionRiskCodes(fresh || [])
    } catch (err) {
      console.error('Failed to refresh intervention risk codes:', err)
    }
    setEditingStrategy(strategy)
    setEditorOpen(true)
  }

  const handleCloseEditor = () => {
    setEditorOpen(false)
    setEditingStrategy(null)
  }

  const handleSaveStrategy = async (strategyData) => {
    try {
      setSaving(true)
      if (editingStrategy) {
        await strategiesAPI.update(editingStrategy.id, strategyData)
      } else {
        const newStrategy = await strategiesAPI.create(strategyData)
        const strategyId = newStrategy.data?.id || newStrategy.id
        await interventionConfigsAPI.addStrategy(interventionId, {
          strategy_id: strategyId,
          is_enabled: true,
        })
      }
      await fetchData()
      handleCloseEditor()
    } catch (err) {
      console.error('Error saving strategy:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveStrategy = async (strategyId) => {
    if (!window.confirm('Remove this composition rule from the intervention?')) return

    try {
      setSaving(true)
      await interventionConfigsAPI.removeStrategy(interventionId, strategyId)
      setAttachedStrategies(attachedStrategies.filter((s) => s.id !== strategyId))
    } catch (err) {
      console.error('Error removing composition rule:', err)
      onError?.('Failed to remove composition rule')
    } finally {
      setSaving(false)
    }
  }

  const persistOrder = async (orderedList) => {
    // Upsert each association with its new priority_order (position in list).
    // addStrategy is ON CONFLICT DO UPDATE, so this is safe to call repeatedly.
    for (let i = 0; i < orderedList.length; i++) {
      const s = orderedList[i]
      await interventionConfigsAPI.addStrategy(interventionId, {
        strategy_id: s.id,
        is_enabled: s.is_enabled,
        priority_order: i,
      })
    }
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    // Firefox requires setData
    try { e.dataTransfer.setData('text/plain', String(index)) } catch (_) { /* noop */ }
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && dropTargetIndex !== index) {
      setDropTargetIndex(index)
    }
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    if (draggedIndex === null || dropTargetIndex === null || draggedIndex === dropTargetIndex) {
      setDraggedIndex(null)
      setDropTargetIndex(null)
      return
    }
    const reordered = [...attachedStrategies]
    const [moved] = reordered.splice(draggedIndex, 1)
    reordered.splice(dropTargetIndex, 0, moved)
    // Optimistic update
    setAttachedStrategies(reordered.map((s, i) => ({ ...s, priority_order: i })))
    setDraggedIndex(null)
    setDropTargetIndex(null)
    try {
      setSaving(true)
      await persistOrder(reordered)
    } catch (err) {
      console.error('Error reordering composition rules:', err)
      onError?.('Failed to save new order')
      await fetchData()
    } finally {
      setSaving(false)
    }
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDropTargetIndex(null)
  }

  const handleToggleEnabled = async (strategy) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.addStrategy(interventionId, {
        strategy_id: strategy.id,
        is_enabled: !strategy.is_enabled,
        priority_order: strategy.priority_order,
      })
      await fetchData()
    } catch (err) {
      console.error('Error updating composition rule:', err)
      onError?.('Failed to update composition rule')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {attachedStrategies.length} composition rule{attachedStrategies.length !== 1 ? 's' : ''} attached
          </Typography>
          {canEdit && attachedStrategies.length > 1 && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6875rem' }}>
              Drag to reorder — lower position evaluates first
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenEditor()}>
            Add Composition Rule
          </Button>
        )}
      </Box>

      {attachedStrategies.length === 0 ? (
        <Alert severity="info">
          No composition rules attached. Add rules to control pathway selection and prioritization.
        </Alert>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {attachedStrategies.map((strategy, index) => {
              const isDragging = draggedIndex === index
              const isDropTarget = dropTargetIndex === index && draggedIndex !== null && draggedIndex !== index
              return (
                <ListItem
                  key={strategy.id}
                  divider={index < attachedStrategies.length - 1}
                  draggable={canEdit}
                  onDragStart={canEdit ? (e) => handleDragStart(e, index) : undefined}
                  onDragOver={canEdit ? (e) => handleDragOver(e, index) : undefined}
                  onDrop={canEdit ? handleDrop : undefined}
                  onDragEnd={canEdit ? handleDragEnd : undefined}
                  sx={{
                    py: 1,
                    px: 2,
                    opacity: isDragging ? 0.4 : (strategy.is_enabled ? 1 : 0.6),
                    borderTop: isDropTarget && draggedIndex > index ? '2px solid' : '2px solid transparent',
                    borderBottom: isDropTarget && draggedIndex < index ? '2px solid' : undefined,
                    borderTopColor: isDropTarget && draggedIndex > index ? 'primary.main' : 'transparent',
                    borderBottomColor: isDropTarget && draggedIndex < index ? 'primary.main' : undefined,
                    transition: 'border-color 0.12s ease',
                  }}
                >
                  {canEdit && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.disabled',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        mr: 1,
                        ml: -0.5,
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                  )}
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {strategy.name}
                        </Typography>
                        {!strategy.strategy_active && (
                          <Typography variant="caption" color="warning.main" fontSize="0.6875rem">
                            Inactive
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      strategy.description && (
                        <Typography variant="caption" color="text.secondary" component="span">
                          {strategy.description}
                        </Typography>
                      )
                    }
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <EnabledIndicator
                      enabled={strategy.is_enabled}
                      onClick={() => canEdit && handleToggleEnabled(strategy)}
                      clickable={canEdit}
                    />
                    {canEdit && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleOpenEditor(strategy)}
                          disabled={saving}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={() => handleRemoveStrategy(strategy.id)}
                          disabled={saving}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
              )
            })}
          </List>
        </Paper>
      )}

      <StrategyEditorDialog
        open={editorOpen}
        onClose={handleCloseEditor}
        strategy={editingStrategy}
        onSave={handleSaveStrategy}
        saving={saving}
        interventionTracks={interventionTracks}
        interventionRiskCodes={interventionRiskCodes}
        allRiskCodes={allRiskCodes}
      />
    </Box>
  )
}

export default StrategiesTab
